import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Handshake, Mail, Phone, ExternalLink, Trash2, MessageSquare, CheckCircle2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminCollaborazioni() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { stato: filter };
      const r = await api.get('/admin/collaborazioni', { params });
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (c, stato) => {
    try { await api.patch(`/admin/collaborazioni/${c.id}`, { stato }); toast.success('Aggiornato'); load(); }
    catch (err) { toast.error('Errore'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la richiesta di ${c.nome_locale}?`)) return;
    await api.delete(`/admin/collaborazioni/${c.id}`);
    toast.success('Eliminata'); load();
  };

  const openDetail = (c) => { setActive(c); setNote(c.note || ''); setOpen(true); };
  const saveNote = async () => {
    try { await api.patch(`/admin/collaborazioni/${active.id}`, { note }); toast.success('Note salvate'); setOpen(false); load(); }
    catch (err) { toast.error('Errore'); }
  };

  return (
    <AdminLayout title="Richieste Collaborazione">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
          <Handshake className="w-4 h-4 text-[color:var(--gold)]"/>
          <span>Richieste B2B dai locali che vogliono collaborare</span>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="collab-filter" className="w-[200px] bg-[color:var(--surface)] border-[color:var(--border-c)]"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="Da contattare">Da contattare</SelectItem>
            <SelectItem value="Contattato">Contattato</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="py-12 text-center text-[color:var(--text-muted)]">Caricamento…</div> : items.length === 0 ? (
        <div className="py-16 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] text-center">
          <Handshake className="w-12 h-12 mx-auto text-[color:var(--gold)] opacity-40 mb-3"/>
          <p className="text-[color:var(--text-muted)]">Nessuna richiesta</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-4" data-testid="collab-row">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Building2 className="w-4 h-4 text-[color:var(--gold)]"/>
                    <h3 className="font-heading text-lg">{c.nome_locale}</h3>
                    <Badge variant="outline" className="text-xs">{c.tipo_richiesta}</Badge>
                    {c.stato === 'Da contattare' ? <Badge className="bg-amber-700/30 text-amber-300">Da contattare</Badge> : <Badge className="bg-emerald-700/30 text-emerald-300">Contattato</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-[color:var(--text-muted)] mb-2">
                    <span>Referente: <b className="text-[color:var(--text)]">{c.referente}</b></span>
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[color:var(--gold)]"><Mail className="w-3 h-3"/>{c.email}</a>
                    <a href={`tel:${c.telefono}`} className="flex items-center gap-1 hover:text-[color:var(--gold)]"><Phone className="w-3 h-3"/>{c.telefono}</a>
                    <span>Ricevuta: {(c.created_at || '').slice(0,16).replace('T', ' ')}</span>
                  </div>
                  <p className="text-sm text-[color:var(--text-muted)] line-clamp-2">{c.messaggio}</p>
                  {c.note && <p className="text-xs text-[color:var(--gold)] mt-2"><MessageSquare className="w-3 h-3 inline mr-1"/>{c.note}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(c)} data-testid="collab-detail"><MessageSquare className="w-4 h-4"/></Button>
                  {c.stato === 'Da contattare' ? (
                    <Button size="sm" data-testid="collab-mark-contacted" onClick={() => updateStatus(c, 'Contattato')} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1"/>Contattato</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(c, 'Da contattare')}>Da contattare</Button>
                  )}
                  <a href={`mailto:${c.email}`} className="text-[color:var(--gold)]"><ExternalLink className="w-4 h-4"/></a>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-red-400 hover:text-red-300" data-testid="collab-delete"><Trash2 className="w-4 h-4"/></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-[color:var(--border-c)] max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{active?.nome_locale}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm text-[color:var(--text-muted)] space-y-1">
                <div><b>Referente:</b> {active.referente}</div>
                <div><b>Email:</b> {active.email}</div>
                <div><b>Telefono:</b> {active.telefono}</div>
                <div><b>Tipo:</b> {active.tipo_richiesta}</div>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1">Messaggio</h4>
                <p className="text-sm bg-[color:var(--surface-2)] border border-[color:var(--border-c)] rounded-lg p-3 whitespace-pre-line">{active.messaggio}</p>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1">Note interne</h4>
                <Textarea data-testid="collab-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]" placeholder="Es. Contattato il 12/03 — interessato a featured 2 settimane…"/>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>Chiudi</Button>
                <Button onClick={saveNote} data-testid="collab-save-note" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black">Salva note</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
